import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Clock, ArrowUpRight, Upload, Instagram, Linkedin } from 'lucide-react';
import Seo from '../components/Seo';
import ContactMap from '../components/ContactMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const NAVY = '#002F6C';

const PROJECT_TYPES = [
  { label: 'landscape', code: 'LA' },
  { label: 'interior design', code: 'ID' },
  { label: 'interior architecture', code: 'IA' },
  { label: 'architecture + landscape', code: 'AL' },
  { label: 'landscape + interior', code: 'LI' },
  { label: 'architecture + masterplanning', code: 'AM' },
  { label: 'architecture + landscape + interior', code: 'ALL' },
  { label: 'other', code: 'OTHER' },
];

const AREAS = [
  'landscape design',
  'horticulture & planting',
  'visualisation & graphics',
  'execution',
  'research & writing',
  'architecture',
  'space design',
  'master planning',
];

// ── small shared field styles ──────────────────────────────────────────────
const labelCls = 'block text-xs text-gray-500 uppercase tracking-wider mb-2';
const labelStyle = { fontFamily: 'Inter, sans-serif', fontWeight: 600 };
const inputCls =
  'w-full px-0 py-3 border-0 border-b border-gray-300 focus:border-[#002F6C] focus:ring-0 bg-transparent lowercase';
const inputStyle = { fontFamily: 'Lato, sans-serif', fontWeight: 300 };

const Door = ({ index, kicker, sanskrit, title, desc, onClick, href, testid }) => {
  const inner = (
    <div
      className="group h-full border border-[#002F6C]/25 bg-white p-8 lg:p-10 flex flex-col cursor-pointer transition-colors duration-300 hover:bg-[#002F6C]"
      data-testid={testid}
    >
      <div className="flex items-start justify-between mb-10">
        <span
          className="text-xs uppercase tracking-[0.2em] text-gray-400 group-hover:text-white/70 transition-colors"
          style={labelStyle}
        >
          {index} — {kicker}
        </span>
        <ArrowUpRight size={22} className="text-[#002F6C] group-hover:text-white transition-colors" />
      </div>
      <p
        className="text-sm italic text-[#002F6C] group-hover:text-white/80 mb-2 transition-colors"
        style={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 500 }}
      >
        {sanskrit}
      </p>
      <h3
        className="text-3xl lg:text-4xl lowercase text-[#3A3530] group-hover:text-white mb-5 transition-colors"
        style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800 }}
      >
        {title}
      </h3>
      <p
        className="text-sm text-gray-600 group-hover:text-white/80 lowercase leading-relaxed transition-colors"
        style={{ fontFamily: 'Lato, sans-serif', fontWeight: 300 }}
      >
        {desc}
      </p>
    </div>
  );
  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className="block h-full">
      {inner}
    </a>
  ) : (
    <div onClick={onClick} className="h-full">{inner}</div>
  );
};

const Contact = () => {
  const [content, setContent] = useState(null);
  const [clock, setClock] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', projectType: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  // sahyog (careers) form
  const [showSahyog, setShowSahyog] = useState(false);
  const [career, setCareer] = useState({ name: '', email: '', phone: '', area: '' });
  const [careerFile, setCareerFile] = useState(null);
  const [careerSubmitting, setCareerSubmitting] = useState(false);
  const sahyogRef = useRef(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/content/landing-page`).then(r => setContent(r.data)).catch(() => {});
  }, []);

  // live ahmedabad clock (Asia/Kolkata)
  useEffect(() => {
    const tick = () => {
      const t = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false,
      }).format(new Date());
      setClock(t);
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);

  const handleChange = (e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const pt = PROJECT_TYPES.find(t => t.code === formData.projectType);
      await axios.post(`${BACKEND_URL}/api/contact/inquiry`, {
        ...formData,
        projectTypeLabel: pt ? pt.label : '',
      });
      toast.success('inquiry sent. we will be in touch soon.');
      setFormData({ name: '', email: '', phone: '', projectType: '', message: '' });
    } catch (err) {
      toast.error('failed to send inquiry. please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const openSahyog = () => {
    setShowSahyog(true);
    setTimeout(() => sahyogRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };

  const submitCareer = async (e) => {
    e.preventDefault();
    if (careerFile && careerFile.size > 7 * 1024 * 1024) {
      toast.error('portfolio file is over 7mb. please upload a smaller file.');
      return;
    }
    setCareerSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', career.name);
      fd.append('email', career.email);
      fd.append('phone', career.phone);
      fd.append('area_of_interest', career.area);
      if (careerFile) fd.append('portfolio', careerFile);
      await axios.post(`${BACKEND_URL}/api/careers/apply`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('application received. thank you — we will reach out.');
      setCareer({ name: '', email: '', phone: '', area: '' });
      setCareerFile(null);
      setShowSahyog(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'failed to submit application. please try again.';
      toast.error(typeof msg === 'string' ? msg : 'failed to submit application. please try again.');
    } finally {
      setCareerSubmitting(false);
    }
  };

  const contactInfo = content?.contact_info || {};
  const studioAddress = content?.studio_address || {};

  return (
    <div className="min-h-screen bg-[#F8F8F7] text-[#3A3530]" data-testid="contact-page">
      <Seo
        title="contact — studio 2+2, Ahmedabad"
        description="studio 2+2, L Block, Mondeal Retail Park, SG Highway, Ahmedabad. mail@twoplustwo.in"
      />

      {/* 1 ── HERO ───────────────────────────────────────────────────────── */}
      <section className="pt-36 pb-20 px-6 md:px-10 lg:px-16">
        <div className="max-w-6xl mx-auto relative">
          <div
            className="absolute top-0 right-0 text-xs text-gray-500 lowercase"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500 }}
            data-testid="contact-clock"
          >
            ahmedabad · {clock} IST
          </div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#002F6C] mb-6" style={labelStyle}>
            let us begin
          </p>
          <h1
            className="text-5xl md:text-6xl lg:text-7xl lowercase mb-8 text-[#3A3530]"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800 }}
          >
            get in touch<span style={{ color: NAVY }}>.</span>
          </h1>
          <p
            className="text-2xl md:text-3xl italic text-gray-600 max-w-3xl leading-snug lowercase"
            style={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 500 }}
          >
            have a project in mind, or want to build with us? pick a door — or simply say hello.
          </p>
        </div>
      </section>

      {/* 2 ── TWO DOORS ──────────────────────────────────────────────────── */}
      <section className="px-6 md:px-10 lg:px-16 pb-10">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
          <Door
            index="01" kicker="clients" sanskrit="sankalp" title="begin a project"
            href="https://sankalpa.vardhaana.com" testid="door-clients"
            desc="tell us about your land and what you imagine. a few questions, and we arrive at the first conversation already understanding you."
          />
          <Door
            index="02" kicker="talent" sanskrit="sahyog" title="join the studio"
            onClick={openSahyog} testid="door-talent"
            desc="for architects, garden designers & thinkers who read the seasons and the soil. share your work — we collaborate, we don't just hire."
          />
        </div>
      </section>

      {/* 3 ── HELLO LINE ─────────────────────────────────────────────────── */}
      <section className="px-6 md:px-10 lg:px-16 pb-20">
        <div className="max-w-6xl mx-auto border-t border-gray-300 pt-6">
          <p className="text-sm text-gray-600 lowercase" style={inputStyle} data-testid="hello-line">
            just saying hello, or press? →{' '}
            <a
              href="mailto:mail@twoplustwo.in"
              className="text-[#002F6C] hover:underline"
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
            >
              mail@twoplustwo.in
            </a>
          </p>
        </div>
      </section>

      {/* 4 + 5 ── INQUIRY FORM + CONTACT PANEL ───────────────────────────── */}
      <section className="px-6 md:px-10 lg:px-16 pb-24">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12">
          {/* inquiry form */}
          <div className="border border-gray-300 bg-white p-8 lg:p-10">
            <h2 className="text-xl text-[#002F6C] lowercase mb-8" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}>
              send us an inquiry
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="inquiry-form">
              <div>
                <label className={labelCls} style={labelStyle}>your name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required
                  className={inputCls} style={inputStyle} placeholder="your name" data-testid="inquiry-name" />
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls} style={labelStyle}>email *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required
                    className={inputCls} style={inputStyle} placeholder="your@email.com" data-testid="inquiry-email" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>phone</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                    className={inputCls.replace(' lowercase', '')} style={inputStyle} placeholder="+91 98765 43210" data-testid="inquiry-phone" />
                </div>
              </div>
              <div>
                <label className={labelCls} style={labelStyle}>project type</label>
                <select name="projectType" value={formData.projectType} onChange={handleChange}
                  className={inputCls} style={inputStyle} data-testid="inquiry-projecttype">
                  <option value="">select a project type</option>
                  {PROJECT_TYPES.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} style={labelStyle}>tell us about your project</label>
                <textarea name="message" value={formData.message} onChange={handleChange} rows={5}
                  className={`${inputCls} resize-none`} style={inputStyle}
                  placeholder="describe your project, timeline, budget range, or any questions you have..." data-testid="inquiry-message" />
              </div>
              <button type="submit" disabled={submitting}
                className="w-full bg-[#002F6C] text-white py-4 hover:bg-[#001f4d] transition-colors disabled:opacity-50 lowercase text-sm"
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }} data-testid="inquiry-submit" data-cursor-label="begin">
                {submitting ? 'sending...' : 'send inquiry'}
              </button>
            </form>
          </div>

          {/* contact panel */}
          <div className="border border-gray-300 bg-white p-8 lg:p-10">
            <h2 className="text-xl text-[#002F6C] lowercase mb-8" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}>
              contact details
            </h2>
            <div className="space-y-8" data-testid="contact-panel">
              {[
                { icon: Mail, title: 'email', value: contactInfo.email || 'mail@twoplustwo.in', href: `mailto:${contactInfo.email || 'mail@twoplustwo.in'}` },
                { icon: Phone, title: 'phone', value: contactInfo.phone || '+91 98765 43210', href: `tel:${contactInfo.phone || '+91 98765 43210'}` },
              ].map(({ icon: Icon, title, value, href }) => (
                <div className="flex items-start gap-4" key={title}>
                  <Icon size={20} className="text-[#002F6C] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm text-gray-900 lowercase mb-1" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}>{title}</h3>
                    <a href={href} className="text-gray-600 hover:text-[#002F6C] transition-colors lowercase" style={inputStyle}>{value}</a>
                  </div>
                </div>
              ))}
              <div className="flex items-start gap-4">
                <MapPin size={20} className="text-[#002F6C] mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-sm text-gray-900 lowercase mb-1" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}>studio</h3>
                  <address className="text-gray-600 not-italic lowercase leading-relaxed" style={inputStyle}>
                    {studioAddress.line1 || 'l block, mondeal retail park,'}<br />
                    {studioAddress.line2 || 'sg highway, ahmedabad,'}<br />
                    {studioAddress.line3 || 'gujarat 380015'}
                  </address>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock size={20} className="text-[#002F6C] mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-sm text-gray-900 lowercase mb-1" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}>hours</h3>
                  <p className="text-gray-600 lowercase" style={inputStyle}>{contactInfo.working_hours || 'mon - sat: 9:00 am - 6:00 pm'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SAHYOG application form (revealed by door 02) */}
      {showSahyog && (
        <section ref={sahyogRef} className="px-6 md:px-10 lg:px-16 pb-24 scroll-mt-28" data-testid="sahyog-section">
          <div className="max-w-3xl mx-auto border border-[#002F6C]/30 bg-white p-8 lg:p-12">
            <p className="text-sm italic text-[#002F6C] mb-1" style={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 500 }}>sahyog</p>
            <h2 className="text-3xl lowercase mb-8 text-[#3A3530]" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800 }}>join the studio</h2>
            <form onSubmit={submitCareer} className="space-y-6" data-testid="sahyog-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls} style={labelStyle}>your name *</label>
                  <input type="text" required value={career.name} onChange={e => setCareer(p => ({ ...p, name: e.target.value }))}
                    className={inputCls} style={inputStyle} placeholder="your name" data-testid="sahyog-name" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>email *</label>
                  <input type="email" required value={career.email} onChange={e => setCareer(p => ({ ...p, email: e.target.value }))}
                    className={inputCls} style={inputStyle} placeholder="your@email.com" data-testid="sahyog-email" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls} style={labelStyle}>phone</label>
                  <input type="tel" value={career.phone} onChange={e => setCareer(p => ({ ...p, phone: e.target.value }))}
                    className={inputCls.replace(' lowercase', '')} style={inputStyle} placeholder="+91 98765 43210" data-testid="sahyog-phone" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>area of interest</label>
                  <select value={career.area} onChange={e => setCareer(p => ({ ...p, area: e.target.value }))}
                    className={inputCls} style={inputStyle} data-testid="sahyog-area">
                    <option value="">select an area</option>
                    {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls} style={labelStyle}>portfolio (pdf or images, max 7mb)</label>
                <label className="flex items-center gap-3 border border-dashed border-gray-400 px-4 py-4 cursor-pointer hover:border-[#002F6C] transition-colors" data-testid="sahyog-upload-label">
                  <Upload size={18} className="text-[#002F6C]" />
                  <span className="text-sm text-gray-600 lowercase" style={inputStyle}>
                    {careerFile ? careerFile.name : 'choose a file to upload'}
                  </span>
                  <input type="file" accept=".pdf,image/png,image/jpeg,image/webp" className="hidden"
                    onChange={e => setCareerFile(e.target.files?.[0] || null)} data-testid="sahyog-file" />
                </label>
              </div>
              <div className="flex gap-4">
                <button type="submit" disabled={careerSubmitting}
                  className="flex-1 bg-[#002F6C] text-white py-4 hover:bg-[#001f4d] transition-colors disabled:opacity-50 lowercase text-sm"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }} data-testid="sahyog-submit">
                  {careerSubmitting ? 'sending...' : 'send application'}
                </button>
                <button type="button" onClick={() => setShowSahyog(false)}
                  className="px-6 border border-gray-300 text-gray-600 hover:border-[#002F6C] hover:text-[#002F6C] transition-colors lowercase text-sm"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }} data-testid="sahyog-cancel">
                  cancel
                </button>
              </div>
            </form>
          </div>
        </section>
      )}

      {/* 6 ── FIND US ────────────────────────────────────────────────────── */}
      <section className="px-6 md:px-10 lg:px-16 pb-24">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-[#002F6C] mb-6" style={labelStyle}>find us — ahmedabad</p>
          <div className="grid md:grid-cols-2 gap-8">
            {/* map */}
            <div data-testid="find-us-map"><ContactMap /></div>
            {/* instagram applet */}
            <div data-testid="instagram-applet" className="flex flex-col">
              <div className="grid grid-cols-3 grid-rows-2 gap-2 aspect-video">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-[#002F6C]/[0.06] border border-[#002F6C]/10 flex items-center justify-center">
                    <Instagram size={22} className="text-[#002F6C]/30" />
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-4">
                <span className="text-sm text-gray-600 lowercase" style={inputStyle}>@studio2p2 · our work, daily</span>
                <a href="https://instagram.com/studio2p2" target="_blank" rel="noopener noreferrer"
                  className="text-sm text-[#002F6C] hover:underline lowercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                  data-testid="instagram-follow">
                  follow →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7 ── FOOTER ─────────────────────────────────────────────────────── */}
      <footer className="border-t border-gray-300 px-6 md:px-10 lg:px-16 py-10">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-sm text-gray-600 lowercase" style={inputStyle} data-testid="footer-line">
            studio 2+2 — taking care of your natural affairs
          </p>
          <div className="flex items-center gap-5">
            <a href="https://instagram.com/studio2p2" target="_blank" rel="noopener noreferrer" aria-label="instagram" className="text-gray-500 hover:text-[#002F6C] transition-colors"><Instagram size={18} /></a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="linkedin" className="text-gray-500 hover:text-[#002F6C] transition-colors"><Linkedin size={18} /></a>
            <a href="mailto:mail@twoplustwo.in" aria-label="email" className="text-gray-500 hover:text-[#002F6C] transition-colors"><Mail size={18} /></a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
